import { useCallback, useEffect, useState } from 'react'
import { Avatar, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Groups } from '@mui/icons-material'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { TextInput } from '$common/input'
import { Label } from '$common/label'
import { WithEditableAvatar } from '$common/with-editable-avatar'
// Localization
import { t } from '$i18n'
// Helper
import { nothing } from '$func/utils'
import { hallConstraints } from '$logic/hall/constant'
import {
  closeSelectWorkspaceName,
  openSelectWorkspaceColor,
} from '$logic/router'
import { getImageUrl } from '$func/imageUrl'
import { showProfilePictureMenu } from '$comp/modals/menus/profile-picture-menu'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'
import { PrimaryButton } from '$common/buttons'
import { useWorkspaceById } from '$logic/workspace'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { usePanelHistoryTop } from 'weblite-router'

const StyledAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  margin: 0 auto;
  background: linear-gradient(180deg, #b3b3b3 0%, #8c8c8c 100%);
`

export function SelectWorkspaceName({ workspaceId }: { workspaceId?: string }) {
  const [name, setName] = useState('')
  const [imageFileId, setImageFileId] = useState<string | undefined>(undefined)

  const panelTop = usePanelHistoryTop('roomProfile')
  useEffect(() => {
    const [topPage] = panelTop?.location?.split('/').slice(-1) ?? []
    if (topPage === 'SELECT_WORKSPACE_NAME' && !workspaceId) {
      setName('')
      setImageFileId(undefined)
    }
  }, [panelTop])

  const { name: defaultName, icon: defaultIcon } =
    useConditionalHook(!!workspaceId, {
      hook: useWorkspaceById,
      hookArgs: workspaceId as string,
      defaultValue: undefined,
    }) || {}

  useEffect(() => {
    if (!defaultName) return
    setName(defaultName)
    setImageFileId(defaultIcon)
  }, [defaultName, defaultIcon])

  const onConfirmHandle = useCallback(async () => {
    analite('SELECT_WORKSPACE_NAME', {
      name,
      icon: imageFileId,
    })

    openSelectWorkspaceColor(name, imageFileId, workspaceId)
    setName('')
    setImageFileId(undefined)
  }, [name, imageFileId, workspaceId])

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitle
          title={t(
            workspaceId
              ? 'selectWorkspaceName.edit'
              : 'selectWorkspaceName.title',
          )}
          onBack={closeSelectWorkspaceName}
        />
      }
    >
      <SettingsSection
        sx={{
          padding: '14px 20px 20px',
        }}
      >
        <>
          <WithEditableAvatar
            onSelectClick={() =>
              showProfilePictureMenu({
                onSubmit: setImageFileId,
                showDelete: !!imageFileId,
              })
            }
          >
            <StyledAvatar src={getImageUrl(imageFileId)} onClick={nothing}>
              <Groups sx={{ width: '42px', height: '42px' }} />
            </StyledAvatar>
          </WithEditableAvatar>

          <Box sx={{ marginTop: '23px' }}>
            <Label title={t('selectWorkspaceName.name')} required />
            <TextInput
              onChange={setName}
              value={name}
              placeholder={t('selectWorkspaceName.placeholder')}
              maxLength={hallConstraints.name.maxLength}
            />
          </Box>
        </>
      </SettingsSection>
      <ButtonWrapper>
        <PrimaryButton
          text={t('selectWorkspaceName.confirm')}
          disabled={!name}
          onClick={onConfirmHandle}
        />
      </ButtonWrapper>
    </PageWithAppBar>
  )
}

const ButtonWrapper = styled(SettingsSection)`
  margin-bottom: 0;
  margin-top: auto;
  padding: 16px;
`
